// The conversation.
//
// One thread — "whichever open thread you participate in", chosen in `snapshot.ts` from
// what the server said this caller participates in and never from the plan's state.
//
// Read back through everything, paginated on sequence: "Read older" asks for one more page
// and re-reads the lot. There is no infinite scroller, because an infinite scroller in a
// thread somebody is reading for the fourth time to find the sentence where their mother
// changed her mind is a way of losing your place.
//
// Pending actions are interleaved by timestamp. See `plan/inline.ts`.
//
// What is not here: no editing, no deleting, no reactions, no read receipts, no typing
// indicator, no presence, no "jump to latest" badge that counts. Unread state IS built —
// see `plan/useMarkThreadRead.ts` and the tab badge in `PlanScreen.tsx` — but it is
// self-facing only: nobody sees another participant's read position, which is what "no read
// receipts" above still means. The facilitator's own state is one line and is in
// `FacilitatorState`.

import { useEffect, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { MessageChoice } from '../api/contract.js';
import { Composer } from '../components/Composer.js';
import { ThreadRoster } from '../components/ThreadRoster.js';
import type { Internals } from '../internals.js';
import { interleave } from '../plan/inline.js';
import { useUploadChoice } from '../plan/uploadChoice.js';
import type { PlanState } from '../plan/usePlan.js';
import { ListeningBubble } from '../components/ListeningBubble.js';
import { useFollowBottom } from '../plan/useFollowBottom.js';
import { useNewItems } from '../plan/useNewItems.js';
import { useMarkThreadRead } from '../plan/useMarkThreadRead.js';
import { useVoiceContext } from '../voice/VoiceContext.js';

export type ConversationProps = {
  client: ApiClient;
  state: PlanState;
  internals: Internals;
  /** The mobile decisions strip (`DecisionsMobileStrip`, built in `PlanScreen.tsx`),
   * rendered directly above the composer so it is actually above it in the document —
   * not merely positioned there with CSS over a sibling laid out beside the conversation.
   * `null` renders nothing; this component knows nothing else about decisions. */
  decisionsStrip?: JSX.Element | null;
  /** The room switcher is showing, so the heading naming this conversation is left out. */
  switcherShown?: boolean;
};

export function Conversation({
  client,
  state,
  internals,
  decisionsStrip,
  switcherShown = false,
}: ConversationProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const [choosing, setChoosing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const upload = useUploadChoice({
    client,
    planId: snapshot?.plan.id ?? '',
    threadId: snapshot?.thread?.id ?? null,
  });

  // `useFollowBottom` is a hook, so it has to be called on every render this component
  // makes, including the ones before a snapshot exists — the null check below can't come
  // first. `lastKey` comes from the interleaved list, not the raw messages, so a pending
  // action arriving with no accompanying message still moves it. `resetKey` is the thread
  // being read; see `useFollowBottom` for why that, and not the plan.
  //
  // Somebody else's ask has no `createdAt` (see `inline.ts`), so it always sits at
  // `items[items.length - 1]` for as long as it stays open — the last item's own key
  // never changes while a message arrives behind it. The count is included so that a
  // message landing anywhere in the thread still moves `lastKey`, trailing ask or not.
  const actions =
    snapshot === null
      ? []
      : snapshot.pendingActions.filter(
          (action) => action.threadId === snapshot.thread?.id,
        );
  const items = snapshot === null ? [] : interleave(snapshot.messages, actions);
  const listening = useVoiceContext();
  const heard = listening?.on === true ? listening.partial : '';
  const lastKey = `${items.length}:${items[items.length - 1]?.key ?? ''}:${heard.length}`;
  const newItems = useNewItems(items, snapshot !== null, snapshot?.thread?.id ?? '');
  const { scroller, atBottom } = useFollowBottom(lastKey, snapshot?.thread?.id ?? '');
  useMarkThreadRead(
    snapshot?.thread?.id ?? null,
    snapshot?.messages ?? [],
    atBottom,
    state.markRead,
  );

  // Tells the voice session which conversation this is; see `PrivateChat`. No target when
  // the thread is closed, because a closed thread has no composer to speak into.
  const voice = useVoiceContext();
  const setVoiceTarget = voice?.setTarget;
  const voiceThreadId = snapshot?.thread?.id ?? null;
  const voiceOpen = snapshot?.thread?.acceptingMessages ?? false;
  const voiceBusy = snapshot?.thread?.facilitatorBusy ?? false;
  const voiceMessages = snapshot?.messages;
  const voicePeople = snapshot?.people;
  const post = state.post;
  useEffect(() => {
    if (setVoiceTarget === undefined) {
      return;
    }
    if (
      voiceThreadId === null ||
      !voiceOpen ||
      voiceMessages === undefined ||
      voicePeople === undefined
    ) {
      setVoiceTarget(null);
      return;
    }
    setVoiceTarget({
      threadId: voiceThreadId,
      messages: voiceMessages,
      people: voicePeople,
      named: true,
      post,
      facilitatorBusy: voiceBusy,
      loaded: true,
    });
  }, [
    setVoiceTarget,
    voiceThreadId,
    voiceOpen,
    voiceMessages,
    voicePeople,
    voiceBusy,
    post,
  ]);
  useEffect(() => () => setVoiceTarget?.(null), [setVoiceTarget]);

  if (snapshot === null) {
    return null;
  }

  const { MessageRow, PendingActionRow, FacilitatorState } = internals;
  const open = snapshot.thread?.acceptingMessages ?? false;
  const newestMessageId = snapshot.messages[snapshot.messages.length - 1]?.id ?? null;
  // Undo on an added-issue card removes the issue by hand, then reads the plan again.
  const added = {
    decisions: snapshot.decisions,
    topics: snapshot.topics,
    thread:
      snapshot.thread === null
        ? null
        : { topic: snapshot.thread.topic, issue: snapshot.thread.issue },
    isPrincipal: snapshot.plan.viewerIsPrincipal,
    busy: removing,
    remove: (decision: string): void => {
      setRemoving(true);
      client
        .changeRecord(snapshot.plan.id, { kind: 'remove_decision', decision })
        .then(() => state.reload())
        .then(
          () => setRemoving(false),
          () => setRemoving(false),
        );
    },
  };
  // A pressed reply is sent as the reader's own message.
  const choose = (choice: MessageChoice): void => {
    if (choice.upload) {
      upload.press();
      return;
    }
    setChoosing(true);
    state.post(choice.text).then(
      () => setChoosing(false),
      () => setChoosing(false),
    );
  };

  return (
    <section className="conversation" aria-label="Conversation">
      {upload.input}
      <ThreadRoster
        people={snapshot.people}
        kind="shared"
        switcherShown={switcherShown}
      />
      <div className="conversation__scroller" ref={scroller}>
        {snapshot.nextBefore === null ? null : (
          <button
            type="button"
            className="button conversation__older"
            onClick={state.loadOlder}
          >
            Read older
          </button>
        )}
        {items.length === 0 && heard === '' ? (
          <p className="conversation__empty">
            Nothing has been said yet. Start wherever you like — Mor will follow.
          </p>
        ) : (
          <ol className="conversation__items">
            {items.map((item) =>
              item.kind === 'message' ? (
                <MessageRow
                  key={item.key}
                  isNew={newItems.has(item.key)}
                  message={item.message}
                  people={snapshot.people}
                  planId={snapshot.plan.id}
                  showAuthor
                  added={added}
                  {...(open && item.message.id === newestMessageId
                    ? {
                        choices: {
                          choose,
                          busy: choosing || state.posting || upload.busy,
                        },
                      }
                    : {})}
                />
              ) : (
                <PendingActionRow
                  key={item.key}
                  isNew={newItems.has(item.key)}
                  action={item.action}
                  people={snapshot.people}
                  topics={snapshot.topics}
                  resolve={state.resolve}
                />
              ),
            )}
            {heard === '' ? null : <ListeningBubble text={heard} />}
          </ol>
        )}
      </div>

      <div className="conversation__foot">
        <FacilitatorState facilitatorBusy={snapshot.thread?.facilitatorBusy ?? false} />
        {upload.problem === null ? null : (
          <p className="artifact__problem" role="alert">
            {upload.problem}
          </p>
        )}
        {decisionsStrip ?? null}
        {open ? (
          <Composer
            posting={state.posting}
            sendFailed={state.sendFailed}
            onSend={state.post}
            {...(snapshot.thread === null ? {} : { threadId: snapshot.thread.id })}
          />
        ) : (
          // A closed thread is a finished conversation, not a broken one. The record stays
          // readable; that is the whole of the `done` state as far as this screen goes.
          <p className="conversation__closed">
            This conversation is closed. Everything said in it stays here to read.
          </p>
        )}
      </div>
    </section>
  );
}
